import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import MediaTypography from 'components/MediaTypography';
import { LanguageTranslationKeys } from 'i18n/languages/en';

import clockIcon from '@common/assets/images/appointments-visual-guide/clock-icon.png';
import laptopIcon from '@common/assets/images/appointments-visual-guide/laptop-icon.png';
import sectionDescriptionImage from '@common/assets/images/appointments-visual-guide/section-description-image.webp';
import usefulInfoImage from '@common/assets/images/appointments-visual-guide/useful-info-image.webp';
import { useServiceRouter } from '@common/components/ServiceRouter';

import { StyledIcon } from '../../styled';
import { IStyleProps } from '../../types';
import { InfoItemWrapper, InfoSectionWrapper, StyledImage } from './styled';

export interface InfoSectionType {
	sectionDescription: LanguageTranslationKeys;
	usefulInfo: LanguageTranslationKeys;
	sessionDuration: LanguageTranslationKeys;
	sessionFormat: LanguageTranslationKeys;
}

interface IProps {
	infoSection: InfoSectionType;
	styleProps?: IStyleProps;
}
function InfoSection({ infoSection, styleProps }: IProps) {
	const { t } = useTranslation();
	const { checkService } = useServiceRouter();
	const isLyceum = checkService('lyceum');

	return (
		<InfoSectionWrapper>
			<InfoItemWrapper>
				{styleProps.sectionDescriptionImage && (
					<StyledImage src={sectionDescriptionImage} alt="description-image" />
				)}
				<Box display="flex" flexDirection="column" gap={1}>
					<MediaTypography variant="subtitle1" color="#333" sx={{ whiteSpace: 'pre-line' }}>
						{isLyceum
							? t('appointment.lyceumVisualGuide.sectionDescriptionTitle')
							: t(infoSection.sectionDescription)}
					</MediaTypography>
					{isLyceum ? (
						<MediaTypography
							color="#333"
							sx={{
								whiteSpace: 'pre-line',
								fontSize: '12px',
								fontWeight: '600',
								lineHeight: '18px',
							}}
						>
							{t('appointment.lyceumVisualGuide.sectionDescription')}
						</MediaTypography>
					) : null}
				</Box>
			</InfoItemWrapper>
			<InfoItemWrapper sx={{ boxShadow: styleProps.boxShadowStyle }}>
				{styleProps.usefulInfoImage && (
					<StyledImage className="sm-down-hidden" src={usefulInfoImage} alt="useful-info" />
				)}
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'space-between' }}>
					<MediaTypography variant="subtitle1" color={styleProps.titlesColor}>
						{isLyceum ? t('appointment.lyceumVisualGuide.usefulInfo') : t(infoSection.usefulInfo)}
					</MediaTypography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<StyledIcon src={clockIcon} alt="clock-icon" />
						<MediaTypography variant="subtitle2" color="#333">
							{isLyceum
								? t('appointment.lyceumVisualGuide.sessionDuration')
								: t(infoSection.sessionDuration)}
						</MediaTypography>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<StyledIcon src={laptopIcon} alt="laptop-icon" />
						<MediaTypography variant="subtitle2" color="#333">
							{t(infoSection.sessionFormat)}
						</MediaTypography>
					</Box>
				</Box>
			</InfoItemWrapper>
		</InfoSectionWrapper>
	);
}

export default InfoSection;
